import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, CalendarCheck, Phone, Tag } from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COOLDOWN_KEY = 'exitPopupDismissedUntil';

const isExternal = (url = '') => /^(https?:|tel:|mailto:|wa\.me)/i.test(url) || url.startsWith('//');

const CTA_META = {
  whatsapp: { icon: WhatsAppIcon, testid: 'exit-popup-cta-whatsapp' },
  book: { icon: CalendarCheck, testid: 'exit-popup-cta-book' },
  call: { icon: Phone, testid: 'exit-popup-cta-call' },
  offers: { icon: Tag, testid: 'exit-popup-cta-offers' },
};

const ExitIntentPopup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const shownRef = useRef(false);
  const timerRef = useRef(null);

  const suppressedPath =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/cv/') ||
    location.pathname === '/enquiry';

  // Fetch config once
  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/exit-popup`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (active && data) setConfig(data); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const cooldownActive = () => {
    try {
      const until = parseInt(localStorage.getItem(COOLDOWN_KEY) || '0', 10);
      return Date.now() < until;
    } catch { return false; }
  };

  // Exit-intent detection
  useEffect(() => {
    if (!config || !config.enabled || suppressedPath || shownRef.current || cooldownActive()) return;

    let fired = false;
    const fire = () => {
      if (fired || shownRef.current) return;
      fired = true;
      const delay = Math.max(0, (config.trigger_delay_seconds || 0) * 1000);
      timerRef.current = setTimeout(() => {
        if (!shownRef.current && !cooldownActive()) {
          shownRef.current = true;
          setOpen(true);
        }
      }, delay);
    };

    // Desktop: mouse leaves through the top of the viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) fire();
    };

    // Mobile: a fast upward scroll (back-intent) near the top of the page
    let lastY = window.scrollY;
    let lastT = Date.now();
    const onScroll = () => {
      const y = window.scrollY;
      const t = Date.now();
      const velocity = (y - lastY) / (t - lastT || 1);
      if (velocity < -1.1 && y < 320) fire();
      lastY = y;
      lastT = t;
    };

    document.addEventListener('mouseout', onMouseOut);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', onScroll);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [config, suppressedPath, location.pathname]);

  const applyCooldown = () => {
    try {
      const days = config?.dismissal_cooldown_days || 0;
      localStorage.setItem(COOLDOWN_KEY, String(Date.now() + days * 86400000));
    } catch { /* ignore */ }
  };

  const close = () => {
    applyCooldown();
    setOpen(false);
  };

  const handleCta = (url) => {
    applyCooldown();
    setOpen(false);
    if (!url) return;
    if (isExternal(url)) {
      window.location.href = url.startsWith('wa.me') ? `https://${url}` : url;
    } else {
      navigate(url);
    }
  };

  if (!open || !config) return null;

  const ctaKeys = ['whatsapp', 'book', 'call', 'offers'].filter((k) => config[k]?.enabled && config[k]?.label);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-rabbit/60 backdrop-blur-sm animate-in fade-in duration-200"
      data-testid="exit-popup-overlay"
      onClick={close}
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-popup-headline"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="exit-popup-close"
          className="absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center rounded-full text-rabbit/70 hover:text-rabbit hover:bg-sky-gray/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-7 md:p-9">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-gray mb-3">restore.dental</p>
          <h2 id="exit-popup-headline" data-testid="exit-popup-headline" className="text-2xl md:text-3xl font-bold text-rabbit leading-tight mb-3">
            {config.headline}
          </h2>
          {config.body && (
            <p className="text-base text-chocolate-malt/80 leading-relaxed mb-5">{config.body}</p>
          )}

          {config.offer_enabled && config.offer_text && (
            <div
              data-testid="exit-popup-offer"
              className="flex items-center gap-3 rounded-xl border border-burnt-coral/30 bg-burnt-coral/5 px-4 py-3 mb-6"
            >
              <Tag className="w-5 h-5 text-burnt-coral shrink-0" />
              <span className="text-sm md:text-base font-semibold text-burnt-coral">{config.offer_text}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ctaKeys.map((key) => {
              const cta = config[key];
              const Icon = CTA_META[key].icon;
              const primary = key === 'book' || key === 'whatsapp';
              return (
                <Button
                  key={key}
                  size="lg"
                  variant={primary ? 'default' : 'outline'}
                  data-testid={CTA_META[key].testid}
                  onClick={() => handleCta(cta.url)}
                  className={
                    primary
                      ? 'w-full bg-burnt-coral hover:bg-burnt-coral/90 text-white justify-start'
                      : 'w-full border-sky-gray/40 text-rabbit hover:bg-sky-gray/10 justify-start'
                  }
                >
                  <Icon className="w-5 h-5 mr-2 shrink-0" />
                  {cta.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
